import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { 
  Bell, 
  BellOff, 
  Check, 
  CheckCheck, 
  Trash2, 
  Calendar, 
  User,
  AlertCircle,
  Filter,
  Search,
  X
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ConfirmationDialog } from "./ui/confirmation-dialog";
import { toast } from "sonner";
import announcementService, { Announcement } from "../services/announcement.service";

interface AnnouncementsPageProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AnnouncementsPage({ open, onOpenChange }: AnnouncementsPageProps) {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [clearAllConfirmation, setClearAllConfirmation] = useState(false);
  const [clearReadConfirmation, setClearReadConfirmation] = useState(false);

  useEffect(() => {
    if (open) {
      fetchAnnouncements();
    }
  }, [open, filter]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await announcementService.getAnnouncements({
        unreadOnly: filter === 'unread',
        limit: 50
      });
      setAnnouncements(response.data.announcements);
      setUnreadCount(response.data.unreadCount || 0);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (announcement: Announcement) => {
    if (announcement.isRead) return;

    try {
      await announcementService.markAsRead(announcement._id);
      setAnnouncements(prev => 
        prev.map(a => 
          a._id === announcement._id 
            ? { ...a, isRead: true, readAt: new Date().toISOString() }
            : a
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      toast.success('Marked as read');
    } catch (error: any) {
      toast.error(error.message || 'Failed to mark as read');
    }
  };

  const handleMarkAsUnread = async (announcement: Announcement) => {
    if (!announcement.isRead) return;

    try {
      await announcementService.markAsUnread(announcement._id);
      setAnnouncements(prev => 
        prev.map(a => 
          a._id === announcement._id 
            ? { ...a, isRead: false, readAt: null }
            : a
        )
      );
      setUnreadCount(prev => prev + 1);
      toast.success('Marked as unread');
    } catch (error: any) {
      toast.error(error.message || 'Failed to mark as unread');
    }
  };

  const handleClearAllClick = () => {
    setClearAllConfirmation(true);
  };

  const handleClearAll = async () => {
    try {
      await announcementService.clearAllAnnouncements();
      setAnnouncements(prev => 
        prev.map(a => ({ ...a, isRead: true, readAt: new Date().toISOString() }))
      );
      setUnreadCount(0);
      toast.success('All announcements marked as read');
    } catch (error: any) {
      toast.error(error.message || 'Failed to clear all announcements');
    }
  };

  const handleClearReadClick = () => {
    setClearReadConfirmation(true);
  };

  const handleClearRead = async () => {
    try {
      await announcementService.clearReadAnnouncements();
      setAnnouncements(prev => prev.filter(a => !a.isRead));
      toast.success('Read announcements cleared');
    } catch (error: any) {
      toast.error(error.message || 'Failed to clear read announcements');
    }
  };

  const handleAnnouncementClick = (announcement: Announcement) => {
    setSelectedAnnouncement(announcement);
    if (!announcement.isRead) {
      handleMarkAsRead(announcement);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'normal': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'low': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const filteredAnnouncements = announcements.filter(announcement => {
    const matchesSearch = announcement.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         announcement.content.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filter === 'read') return matchesSearch && announcement.isRead;
    if (filter === 'unread') return matchesSearch && !announcement.isRead;
    return matchesSearch;
  });

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl w-[90vw] max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0f1f3d] flex items-center gap-2">
              <Bell className="w-6 h-6 text-[#d4af37]" />
              Announcements
              {unreadCount > 0 && (
                <Badge className="bg-red-500 text-white">
                  {unreadCount} new
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Header Actions */}
            <div className="flex flex-col gap-3 mb-6">
              {/* Search */}
              <div className="relative flex items-center">
                <Search className="absolute left-3 text-gray-400 w-4 h-4 z-10" />
                <Input
                  placeholder="Search announcements..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full bg-white border border-gray-300 rounded-md focus-visible:border-[#d4af37] focus-visible:ring-1 focus-visible:ring-[#d4af37]"
                />
              </div>
              
              {/* Filter */}
              <Select value={filter} onValueChange={(value: any) => setFilter(value)}>
                <SelectTrigger className="w-full bg-white border border-gray-300 hover:bg-gray-50 focus:border-[#d4af37] focus:ring-1 focus:ring-[#d4af37]">
                  <Filter className="w-4 h-4 mr-2 flex-shrink-0" />
                  <SelectValue placeholder="Filter..." />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-300 shadow-lg min-w-[170px] p-1">
                  <SelectItem value="all" className="hover:bg-gray-100 focus:bg-gray-100 cursor-pointer data-[state=checked]:bg-[#d4af37] data-[state=checked]:text-[#0f1f3d]">
                    All Announcements
                  </SelectItem>
                  <SelectItem value="unread" className="hover:bg-gray-100 focus:bg-gray-100 cursor-pointer data-[state=checked]:bg-[#d4af37] data-[state=checked]:text-[#0f1f3d]">
                    Unread Only
                  </SelectItem>
                  <SelectItem value="read" className="hover:bg-gray-100 focus:bg-gray-100 cursor-pointer data-[state=checked]:bg-[#d4af37] data-[state=checked]:text-[#0f1f3d]">
                    Read Only
                  </SelectItem>
                </SelectContent>
              </Select>
              
              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleClearAllClick}
                  disabled={unreadCount === 0}
                  className="flex-1"
                >
                  <CheckCheck className="w-4 h-4 mr-2" />
                  Mark All Read
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleClearReadClick}
                  className="flex-1 text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear Read
                </Button>
              </div>
            </div>

            {/* Announcements List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#d4af37]"></div>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredAnnouncements.map((announcement) => (
                    <motion.div
                      key={announcement._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`border rounded-lg p-4 cursor-pointer hover:shadow-md transition-all ${
                        !announcement.isRead 
                          ? 'bg-blue-50 border-blue-200 shadow-sm' 
                          : 'bg-white hover:bg-gray-50'
                      }`}
                      onClick={() => handleAnnouncementClick(announcement)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {!announcement.isRead && (
                              <div className="w-2 h-2 bg-[#d4af37] rounded-full"></div>
                            )}
                            <h3 className={`font-semibold ${
                              !announcement.isRead ? 'text-[#0f1f3d]' : 'text-gray-700'
                            }`}>
                              {announcement.title}
                            </h3>
                            <Badge className={getPriorityColor(announcement.priority)}>
                              {announcement.priority}
                            </Badge>
                          </div>
                          <p className="text-gray-600 text-sm mb-2 line-clamp-2">
                            {announcement.content}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center">
                              <User className="w-3 h-3 mr-1" />
                              {announcement.author.name}
                            </span>
                            <span className="flex items-center">
                              <Calendar className="w-3 h-3 mr-1" />
                              {new Date(announcement.createdAt).toLocaleDateString()}
                            </span>
                            {announcement.expiresAt && (
                              <span className="flex items-center text-orange-600">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Expires {new Date(announcement.expiresAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1 ml-4">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              announcement.isRead 
                                ? handleMarkAsUnread(announcement)
                                : handleMarkAsRead(announcement);
                            }}
                            className="p-1"
                          >
                            {announcement.isRead ? (
                              <BellOff className="w-4 h-4 text-gray-400" />
                            ) : (
                              <Check className="w-4 h-4 text-[#d4af37]" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                  
                  {filteredAnnouncements.length === 0 && !loading && (
                    <div className="text-center py-8 text-gray-500">
                      {searchTerm ? (
                        'No announcements found matching your search.'
                      ) : filter === 'unread' ? (
                        'No unread announcements.'
                      ) : filter === 'read' ? (
                        'No read announcements.'
                      ) : (
                        'No announcements yet.'
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Announcement Detail Modal */}
      <Dialog 
        open={!!selectedAnnouncement} 
        onOpenChange={(open) => !open && setSelectedAnnouncement(null)}
      >
        <DialogContent className="max-w-2xl">
          {selectedAnnouncement && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="text-xl font-bold text-[#0f1f3d] flex items-center gap-2">
                    {selectedAnnouncement.title}
                    <Badge className={getPriorityColor(selectedAnnouncement.priority)}>
                      {selectedAnnouncement.priority}
                    </Badge>
                  </DialogTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedAnnouncement(null)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4 text-sm text-gray-500 border-b pb-3">
                  <span className="flex items-center">
                    <User className="w-4 h-4 mr-1" />
                    From: {selectedAnnouncement.author.name}
                  </span>
                  <span className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    {new Date(selectedAnnouncement.createdAt).toLocaleDateString()}
                  </span>
                  {selectedAnnouncement.expiresAt && (
                    <span className="flex items-center text-orange-600">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      Expires {new Date(selectedAnnouncement.expiresAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
                
                <div className="prose prose-sm max-w-none">
                  <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                    {selectedAnnouncement.content}
                  </p>
                </div>
                
                <div className="flex justify-between items-center pt-4 border-t">
                  <div className="text-sm text-gray-500">
                    {selectedAnnouncement.isRead ? (
                      <span className="flex items-center text-green-600">
                        <Check className="w-4 h-4 mr-1" />
                        Read on {selectedAnnouncement.readAt && new Date(selectedAnnouncement.readAt).toLocaleDateString()}
                      </span>
                    ) : (
                      <span className="flex items-center text-[#d4af37]">
                        <Bell className="w-4 h-4 mr-1" />
                        Unread
                      </span>
                    )}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      selectedAnnouncement.isRead 
                        ? handleMarkAsUnread(selectedAnnouncement)
                        : handleMarkAsRead(selectedAnnouncement);
                    }}
                  >
                    {selectedAnnouncement.isRead ? (
                      <>
                        <BellOff className="w-4 h-4 mr-2" />
                        Mark Unread
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Mark Read
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialogs */}
      <ConfirmationDialog
        open={clearAllConfirmation}
        onOpenChange={setClearAllConfirmation}
        title="Mark All as Read"
        description="Are you sure you want to mark all announcements as read?"
        confirmText="Mark All Read"
        cancelText="Cancel"
        onConfirm={handleClearAll}
      />

      <ConfirmationDialog
        open={clearReadConfirmation}
        onOpenChange={setClearReadConfirmation}
        title="Clear Read Announcements"
        description="Are you sure you want to remove all read announcements from your list? This action cannot be undone."
        confirmText="Clear Read"
        cancelText="Cancel"
        onConfirm={handleClearRead}
        variant="destructive"
      />
    </>
  );
}
